import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AircraftRegistrationLookup, normalizeRegistration } from './AircraftRegistrationLookup.js';
import { AerodromeLookup } from './AerodromeLookup.js';

export interface OrganizationResources {
  readonly bases: readonly BaseResource[];
  readonly aircraft: readonly AircraftResource[];
}

interface BaseResource {
  readonly id: string;
  readonly name: string;
  readonly airportCode: string | null;
  readonly active: boolean;
  readonly updatedAt: string;
}

interface AircraftResource {
  readonly baseIds: string[];
  readonly rabVerified: boolean;
  readonly aircraftKind?: 'airplane' | 'helicopter' | null;
  readonly id: string;
  readonly registrationMark: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string | null;
  readonly active: boolean;
  readonly relationships: {
    readonly legalOwner: string | null;
    readonly aircraftManager: string | null;
    readonly responsibleOperator: string | null;
    readonly coordinationResponsible: string | null;
  };
  readonly eligibility: {
    readonly listedInOperationsSpecifications: boolean;
    readonly inclusionStatus: string;
    readonly privateEligible: boolean;
    readonly commercialEligible: boolean;
    readonly operationallyAvailable: boolean;
    readonly technicalBlocked: boolean;
    readonly reason: string | null;
  };
  readonly updatedAt: string;
}

interface BaseDraft {
  readonly id: string;
  readonly name: string;
  readonly airportCode: string;
  readonly active: boolean;
  readonly expectedUpdatedAt: string | null;
}

interface AircraftDraft {
  readonly baseIds: string[];
  readonly aircraftKind: '' | 'airplane' | 'helicopter';
  readonly id: string;
  readonly registrationMark: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly active: boolean;
  readonly legalOwner: string;
  readonly aircraftManager: string;
  readonly responsibleOperator: string;
  readonly coordinationResponsible: string;
  readonly listedInOperationsSpecifications: boolean;
  readonly inclusionStatus: string;
  readonly privateEligible: boolean;
  readonly commercialEligible: boolean;
  readonly operationallyAvailable: boolean;
  readonly technicalBlocked: boolean;
  readonly eligibilityReason: string;
  readonly expectedUpdatedAt: string | null;
}

const newBase = (): BaseDraft => ({
  id: crypto.randomUUID(),
  name: '',
  airportCode: '',
  active: true,
  expectedUpdatedAt: null,
});

const newAircraft = (): AircraftDraft => ({
  baseIds: [],
  aircraftKind: '',
  id: crypto.randomUUID(),
  registrationMark: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  active: true,
  legalOwner: '',
  aircraftManager: '',
  responsibleOperator: '',
  coordinationResponsible: '',
  listedInOperationsSpecifications: false,
  inclusionStatus: 'not_applicable',
  privateEligible: false,
  commercialEligible: false,
  operationallyAvailable: false,
  technicalBlocked: false,
  eligibilityReason: '',
  expectedUpdatedAt: null,
});

function toAircraftDraft(resource: AircraftResource): AircraftDraft {
  return {
    baseIds: resource.baseIds || [],
    aircraftKind: resource.aircraftKind || '',
    id: resource.id,
    registrationMark: resource.registrationMark,
    manufacturer: resource.manufacturer,
    model: resource.model,
    serialNumber: resource.serialNumber ?? '',
    active: resource.active,
    legalOwner: resource.relationships.legalOwner ?? '',
    aircraftManager: resource.relationships.aircraftManager ?? '',
    responsibleOperator: resource.relationships.responsibleOperator ?? '',
    coordinationResponsible: resource.relationships.coordinationResponsible ?? '',
    listedInOperationsSpecifications: resource.eligibility.listedInOperationsSpecifications,
    inclusionStatus: resource.eligibility.inclusionStatus,
    privateEligible: resource.eligibility.privateEligible,
    commercialEligible: resource.eligibility.commercialEligible,
    operationallyAvailable: resource.eligibility.operationallyAvailable,
    technicalBlocked: resource.eligibility.technicalBlocked,
    eligibilityReason: resource.eligibility.reason ?? '',
    expectedUpdatedAt: resource.updatedAt,
  };
}

function optional(value: string): string | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function OrganizationSetup({
  organizationId,
  membersActive,
  invitationsPending,
  operationsTotal,
  onChanged,
  onExpiredSession,
  onStartOperation,
}: {
  readonly organizationId: string;
  readonly membersActive: number;
  readonly invitationsPending: number;
  readonly operationsTotal: number;
  readonly onChanged: () => Promise<void> | void;
  readonly onExpiredSession: () => Promise<void>;
  readonly onStartOperation: () => void;
}) {
  const [resources, setResources] = useState<OrganizationResources>();
  const [baseDraft, setBaseDraft] = useState<BaseDraft>(newBase);
  const [aircraftDraft, setAircraftDraft] = useState<AircraftDraft>(newAircraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<'base' | 'aircraft'>();
  const [verifiedMark, setVerifiedMark] = useState('');
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/resources`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw new Error(String(response.status));
      setResources((await response.json()) as OrganizationResources);
      setError(undefined);
    } catch {
      setError('Não foi possível carregar os recursos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [onExpiredSession, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (kind: 'base' | 'aircraft', id: string, body: object) => {
    setSaving(kind);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(
        `/v1/organizations/${organizationId}/admin/${kind === 'base' ? 'bases' : 'aircraft'}/${id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (response.status === 401) {
        await onExpiredSession();
        return false;
      }
      if (response.status === 409) {
        const conflict = (await response.json().catch(() => null)) as { code?: string } | null;
        setError(
          conflict?.code === 'BASE_RESOURCE_ALREADY_EXISTS'
            ? 'Já existe uma base com esse nome nesta organização.'
            : conflict?.code === 'AIRCRAFT_RESOURCE_ALREADY_EXISTS'
              ? 'Já existe uma aeronave com essa matrícula nesta organização.'
              : 'Esse cadastro foi alterado em outra sessão. Recarregue os dados antes de salvar.',
        );
        return false;
      }
      if (!response.ok) {
        setError(
          kind === 'aircraft'
            ? 'Revise os dados e as pré-condições cadastrais da aeronave.'
            : 'Revise o nome e o código do aeródromo.',
        );
        return false;
      }
      await load();
      await onChanged();
      setFeedback(
        kind === 'base'
          ? 'Base salva.'
          : 'Aeronave salva com histórico de vínculos e elegibilidade.',
      );
      return true;
    } catch {
      setError('Não foi possível salvar. Seus dados continuam no formulário.');
      return false;
    } finally {
      setSaving(undefined);
    }
  };

  const handleBaseSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const saved = await save('base', baseDraft.id, {
      name: baseDraft.name,
      airportCode: optional(baseDraft.airportCode)?.toUpperCase() ?? null,
      active: baseDraft.active,
      expectedUpdatedAt: baseDraft.expectedUpdatedAt,
    });
    if (saved) setBaseDraft(newBase());
  };

  const handleAircraftSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !verifiedMark ||
      verifiedMark !== normalizeRegistration(aircraftDraft.registrationMark) ||
      !aircraftDraft.baseIds.length
    ) {
      setError('Consulte a matrícula no RAB e selecione pelo menos uma base.');
      return;
    }
    const saved = await save('aircraft', aircraftDraft.id, {
      ...aircraftDraft,
      registrationMark: aircraftDraft.registrationMark.toUpperCase(),
      serialNumber: optional(aircraftDraft.serialNumber),
      legalOwner: optional(aircraftDraft.legalOwner),
      aircraftManager: optional(aircraftDraft.aircraftManager),
      responsibleOperator: optional(aircraftDraft.responsibleOperator),
      coordinationResponsible: optional(aircraftDraft.coordinationResponsible),
      eligibilityReason: optional(aircraftDraft.eligibilityReason),
    });
    if (saved) {
      setAircraftDraft(newAircraft());
      setVerifiedMark('');
    }
  };

  const activeBases = resources?.bases.filter((item) => item.active).length ?? 0;
  const activeAircraft = resources?.aircraft.filter((item) => item.active).length ?? 0;
  const steps = [
    { label: 'Cadastrar uma base', done: activeBases > 0 },
    { label: 'Cadastrar uma aeronave', done: activeAircraft > 0 },
    { label: 'Convidar a equipe', done: membersActive > 1 || invitationsPending > 0 },
    { label: 'Criar a primeira operação', done: operationsTotal > 0 },
  ];
  const completed = steps.filter((step) => step.done).length;

  return (
    <section className="organizationSetup" aria-labelledby="organization-setup-title">
      <div className="setupHeader">
        <div>
          <span className="sectionEyebrow">Comece por aqui</span>
          <h2 id="organization-setup-title">Preparação da operação</h2>
          <p>
            Cadastre o contexto mínimo para a equipe operar sem depender de configuração técnica.
          </p>
        </div>
        <strong>
          {completed} de {steps.length} etapas
        </strong>
      </div>
      <ol className="setupSteps" aria-label="Progresso do onboarding">
        {steps.map((step, index) => (
          <li className={step.done ? 'isComplete' : ''} key={step.label}>
            <span>{step.done ? '✓' : index + 1}</span>
            {step.label}
          </li>
        ))}
      </ol>

      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : null}
      {feedback ? (
        <p className="invitationFeedback invitationFeedback-success" role="status">
          {feedback}
        </p>
      ) : null}
      {loading ? (
        <p className="setupLoading" role="status">
          Carregando cadastros…
        </p>
      ) : null}

      <div className="resourceColumns">
        <section className="resourcePanel" aria-labelledby="bases-title">
          <div className="resourcePanelHeading">
            <div>
              <span className="sectionEyebrow">Bases</span>
              <h3 id="bases-title">Locais da operação</h3>
            </div>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => setBaseDraft(newBase())}
            >
              Nova base
            </button>
          </div>
          <form className="resourceForm" onSubmit={(event) => void handleBaseSubmit(event)}>
            <AerodromeLookup
              label="Encontrar local na ANAC"
              onExpiredSession={onExpiredSession}
              onSelect={(suggestion) =>
                setBaseDraft((current) => ({
                  ...current,
                  name: suggestion.name,
                  airportCode: suggestion.icaoCode ?? suggestion.iataCode ?? '',
                }))
              }
            />
            <label>
              Nome da base
              <input
                required
                minLength={2}
                maxLength={120}
                value={baseDraft.name}
                onChange={(event) =>
                  setBaseDraft((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>
            <label>
              Código ICAO ou local
              <input
                maxLength={4}
                placeholder="SBSP"
                value={baseDraft.airportCode}
                onChange={(event) =>
                  setBaseDraft((current) => ({
                    ...current,
                    airportCode: event.target.value.toUpperCase(),
                  }))
                }
              />
            </label>
            <label className="checkField">
              <input
                type="checkbox"
                checked={baseDraft.active}
                onChange={(event) =>
                  setBaseDraft((current) => ({ ...current, active: event.target.checked }))
                }
              />
              Base ativa
            </label>
            <button className="primaryButton" disabled={saving === 'base'} type="submit">
              {saving === 'base'
                ? 'Salvando…'
                : baseDraft.expectedUpdatedAt
                  ? 'Atualizar base'
                  : 'Cadastrar base'}
            </button>
          </form>
          <div className="resourceList">
            {resources?.bases.map((base) => (
              <article key={base.id}>
                <div>
                  <strong>{base.name}</strong>
                  <span>
                    {base.airportCode ?? 'Local ZZZZ'} · {base.active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setBaseDraft({
                      id: base.id,
                      name: base.name,
                      airportCode: base.airportCode ?? '',
                      active: base.active,
                      expectedUpdatedAt: base.updatedAt,
                    })
                  }
                >
                  Editar
                </button>
              </article>
            ))}
            {resources?.bases.length === 0 ? <p>Nenhuma base cadastrada.</p> : null}
          </div>
        </section>

        <section className="resourcePanel" aria-labelledby="aircraft-title">
          <div className="resourcePanelHeading">
            <div>
              <span className="sectionEyebrow">Aeronaves</span>
              <h3 id="aircraft-title">Frota e pré-condições</h3>
            </div>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setAircraftDraft(newAircraft());
                setVerifiedMark('');
              }}
            >
              Nova aeronave
            </button>
          </div>
          <form
            className="resourceForm aircraftResourceForm"
            onSubmit={(event) => void handleAircraftSubmit(event)}
          >
            <AircraftRegistrationLookup
              key={aircraftDraft.id}
              value={aircraftDraft}
              onChange={(identity) => setAircraftDraft((current) => ({ ...current, ...identity }))}
              onVerified={setVerifiedMark}
              onExpiredSession={onExpiredSession}
            />
            <fieldset>
              <legend>
                Bases desta aeronave <span className="requiredMark">*</span>
              </legend>
              {resources?.bases
                .filter((base) => base.active)
                .map((base) => (
                  <label className="checkField" key={base.id}>
                    <input
                      type="checkbox"
                      checked={aircraftDraft.baseIds.includes(base.id)}
                      onChange={(event) =>
                        setAircraftDraft((current) => ({
                          ...current,
                          baseIds: event.target.checked
                            ? [...current.baseIds, base.id]
                            : current.baseIds.filter((id) => id !== base.id),
                        }))
                      }
                    />
                    {base.name} {base.airportCode}
                  </label>
                ))}
              <p>
                Selecione uma ou mais bases. Para transferir, desmarque a base anterior e marque a
                nova.
              </p>
            </fieldset>
            <fieldset>
              <legend>Vínculos cadastrais</legend>
              <div className="resourceFormGrid">
                <label>
                  Proprietário
                  <input
                    maxLength={160}
                    value={aircraftDraft.legalOwner}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        legalOwner: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Gestor da aeronave
                  <input
                    maxLength={160}
                    value={aircraftDraft.aircraftManager}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        aircraftManager: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Operador responsável (EO)
                  <input
                    maxLength={160}
                    value={aircraftDraft.responsibleOperator}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        responsibleOperator: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Responsável pela coordenação
                  <input
                    maxLength={160}
                    value={aircraftDraft.coordinationResponsible}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        coordinationResponsible: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Pré-condições cadastrais</legend>
              <div className="eligibilityGrid">
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={aircraftDraft.listedInOperationsSpecifications}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        listedInOperationsSpecifications: event.target.checked,
                      }))
                    }
                  />
                  Consta nas EO
                </label>
                <label>
                  Status nas EO
                  <select
                    value={aircraftDraft.inclusionStatus}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        inclusionStatus: event.target.value,
                      }))
                    }
                  >
                    <option value="not_applicable">Não aplicável</option>
                    <option value="not_started">Não iniciado</option>
                    <option value="in_progress">Em andamento</option>
                    <option value="approved">Aprovado</option>
                    <option value="rejected">Rejeitado</option>
                  </select>
                </label>
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={aircraftDraft.privateEligible}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        privateEligible: event.target.checked,
                      }))
                    }
                  />
                  Elegível para privada
                </label>
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={aircraftDraft.commercialEligible}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        commercialEligible: event.target.checked,
                      }))
                    }
                  />
                  Elegível para comercial
                </label>
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={aircraftDraft.operationallyAvailable}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        operationallyAvailable: event.target.checked,
                      }))
                    }
                  />
                  Disponível no cadastro
                </label>
                <label className="checkField">
                  <input
                    type="checkbox"
                    checked={aircraftDraft.technicalBlocked}
                    onChange={(event) =>
                      setAircraftDraft((current) => ({
                        ...current,
                        technicalBlocked: event.target.checked,
                      }))
                    }
                  />
                  Bloqueio técnico informado
                </label>
              </div>
              <label>
                Motivo ou observação
                <textarea
                  maxLength={500}
                  value={aircraftDraft.eligibilityReason}
                  onChange={(event) =>
                    setAircraftDraft((current) => ({
                      ...current,
                      eligibilityReason: event.target.value,
                    }))
                  }
                />
              </label>
              <small>
                Esses dados são pré-condições cadastrais e não representam autorização operacional.
              </small>
            </fieldset>
            <label className="checkField">
              <input
                type="checkbox"
                checked={aircraftDraft.active}
                onChange={(event) =>
                  setAircraftDraft((current) => ({ ...current, active: event.target.checked }))
                }
              />
              Aeronave ativa
            </label>
            <button
              className="primaryButton"
              disabled={saving === 'aircraft' || !verifiedMark || !aircraftDraft.baseIds.length}
              type="submit"
            >
              {saving === 'aircraft'
                ? 'Salvando…'
                : aircraftDraft.expectedUpdatedAt
                  ? 'Atualizar aeronave'
                  : 'Cadastrar aeronave'}
            </button>
          </form>
          <div className="resourceList">
            {resources?.aircraft.map((aircraft) => (
              <article key={aircraft.id}>
                <div>
                  <strong>
                    {aircraft.registrationMark} · {aircraft.model}
                  </strong>
                  <span>
                    {(aircraft.baseIds || [])
                      .map((id) => resources.bases.find((base) => base.id === id)?.name)
                      .filter(Boolean)
                      .join(' · ')}
                    {' — '}
                    {aircraft.eligibility.commercialEligible
                      ? 'Pré-condições comerciais registradas'
                      : 'Sem pré-condição comercial'}{' '}
                    · {aircraft.active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAircraftDraft(toAircraftDraft(aircraft));
                    setVerifiedMark(
                      aircraft.rabVerified ? normalizeRegistration(aircraft.registrationMark) : '',
                    );
                  }}
                >
                  Editar
                </button>
              </article>
            ))}
            {resources?.aircraft.length === 0 ? <p>Nenhuma aeronave cadastrada.</p> : null}
          </div>
        </section>
      </div>
      {completed === steps.length ? (
        <div className="setupComplete">
          <div>
            <strong>Configuração inicial concluída</strong>
            <span>A equipe já pode iniciar a primeira jornada operacional.</span>
          </div>
          <button className="primaryButton" type="button" onClick={onStartOperation}>
            Criar operação
          </button>
        </div>
      ) : null}
    </section>
  );
}
